import { useEffect, useState, useCallback, useRef } from "react";
import {
    PassioApiClient,
    createPassioApiClient,
    PassioApiClientConfig,
} from "../services/passioApiClient";
import { useAuth } from "../contexts/AuthContext";
import { APP_CONFIG } from "../config/app.config";

import { hasServiceAccess } from "../utils";

export const usePassioApi = () => {
    const { dbUser, refreshUser } = useAuth();
    const [client, setClient] = useState<PassioApiClient | null>(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const clientRef = useRef<PassioApiClient | null>(null);
    const currentApiKeyRef = useRef<string | null>(null);

    // Determine which API key to use
    const getApiKey = useCallback(() => {
        // If user has their own API key, use it
        if (dbUser?.passioapikey) {
            return dbUser.passioapikey;
        }
        
        // If user has used free trial (has credit card), they can use the free trial API key
        // This allows food search to work even when they have no free runs left
        if (dbUser && dbUser.has_used_free_trial && APP_CONFIG.FREE_TRIAL_API_KEY) {
            return APP_CONFIG.FREE_TRIAL_API_KEY;
        }
        
        return null;
    }, [dbUser]);

    // Initialize or update the client
    const initializeClient = useCallback(() => {
        const apiKey = getApiKey();
        
        if (!apiKey) {
            // No API key available, clear the client
            if (clientRef.current) {
                clientRef.current.destroy();
                clientRef.current = null;
                setClient(null);
                currentApiKeyRef.current = null;
            }
            return;
        }

        // If we already have a client with the same API key, don't recreate
        if (clientRef.current && currentApiKeyRef.current === apiKey) {
            return;
        }

        setIsInitializing(true);

        // Destroy old client if it exists
        if (clientRef.current) {
            clientRef.current.destroy();
        }

        // Create new client with appropriate API key
        const config: PassioApiClientConfig = {
            apiKey: apiKey,
            baseUrl: process.env.REACT_APP_PASSIO_API_HOST,
            defaultCacheTtl: 10, // 10 minutes default cache
        };

        const newClient = createPassioApiClient(config);
        clientRef.current = newClient;
        currentApiKeyRef.current = apiKey;
        setClient(newClient);
        setIsInitializing(false);
    }, [getApiKey]);

    // Note: Free run decrement has been moved to happen immediately when tasks are submitted
    // rather than after successful completion. This prevents issues with task-based generation
    // where the completion happens asynchronously via webhooks.
    const decrementFreeRunIfNeeded = useCallback(async (endpoint: string, response: any) => {
        // This function is kept for backward compatibility but no longer decrements free runs
        // Free runs are now decremented immediately in GenerateMealPlan component
        return;
    }, []);

    // Wrap API methods to add free run decrementing
    const wrapApiMethod = useCallback(<T extends (...args: any[]) => Promise<any>>(
        method: T,
        endpoint: string
    ): T => {
        return (async (...args: Parameters<T>) => {
            try {
                const result = await method.apply(client, args);
                // Decrement free run if needed
                await decrementFreeRunIfNeeded(endpoint, result);
                return result;
            } catch (error) {
                // If error is due to no access, refresh user to ensure we have latest data
                if (error instanceof Error && error.message.includes('403')) {
                    await refreshUser();
                }
                throw error;
            }
        }) as T;
    }, [client, decrementFreeRunIfNeeded, refreshUser]);

    // Create wrapped client with instrumented methods
    const wrappedClient = useCallback(() => {
        if (!client) return null;
        
        // Create a proxy to wrap tool methods
        return new Proxy(client, {
            get(target, prop) {
                const value = target[prop as keyof PassioApiClient];
                
                // Wrap methods that call /tools/ endpoints
                if (typeof value === 'function' && typeof prop === 'string') {
                    // List of methods that call tool endpoints
                    const toolMethods = [
                        'generateMealPlan',
                        'generateMealPlanPreview',
                        'regenerateMeals',
                        'convertMealPlanToAffiliateLink'
                    ];
                    
                    if (toolMethods.includes(prop)) {
                        return wrapApiMethod(value.bind(target) as any, `/tools/${prop}`);
                    }
                    
                    // For all other methods, bind the context
                    return value.bind(target);
                }
                
                return value;
            }
        }) as PassioApiClient;
    }, [client, wrapApiMethod]);

    // Initialize client when user changes or API key changes
    useEffect(() => {
        initializeClient();
    }, [initializeClient]);

    // Create task-based wrapper for meal plan generation
    const generateMealPlanWithTask = async (
        prompt: string,
        metadata: { profileId?: number | null; numberOfDays?: number } = {},
        queryParams: Record<string, string> = {},
        useTaskWorker: boolean = false
    ): Promise<any> => {
        if (!client) throw new Error("Client not initialized");
        
        if (!useTaskWorker) {
            // Use synchronous generation
            return client.generateMealPlan(prompt, {}, queryParams);
        }

        // Use task-based generation
        const result = await client.submitMealPlanTask(
            prompt,
            metadata,
            queryParams
        );

        return result;
    };

    // Create task-based wrapper for meal plan preview generation
    const generateMealPlanPreviewWithTask = async (
        prompt: string,
        metadata: { profileId?: number | null } = {},
        queryParams: Record<string, string> = {},
        useTaskWorker: boolean = false
    ): Promise<any> => {
        if (!client) throw new Error("Client not initialized");
        
        if (!useTaskWorker) {
            // Use synchronous generation
            return client.generateMealPlanPreview(prompt, {}, queryParams);
        }

        // Use task-based generation
        const result = await client.submitMealPlanPreviewTask(
            prompt,
            metadata,
            queryParams
        );

        return result;
    };

    // Check if user has access
    const hasAccess = hasServiceAccess(dbUser);

    return {
        client: wrappedClient(),
        isInitializing,
        hasApiKey: !!getApiKey(),
        hasAccess,
        freeRunsRemaining: dbUser?.free_runs_remaining || 0,
        isUsingFreeTrial: !dbUser?.passioapikey,
        generateMealPlanWithTask,
        generateMealPlanPreviewWithTask,
    };
};
